import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Save, X, Info, Download, Upload, Search, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { workflowsApi } from '@/lib/api';
import { VariablesPageSkeleton } from '@/components/ui/page-skeletons';

interface Variable {
  key: string;
  value: any;
  type: 'string' | 'number' | 'boolean' | 'json';
  description?: string;
}

interface Workflow {
  id: string;
  name: string;
  variables: Record<string, any>;
}

export default function Variables() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [localVariables, setLocalVariables] = useState<Variable[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  useEffect(() => {
    loadWorkflows();
  }, []);

  useEffect(() => {
    const workflowId = searchParams.get('workflow');
    if (workflowId && workflows.length > 0) {
      setSelectedWorkflowId(workflowId);
      const workflow = workflows.find(w => w.id === workflowId);
      if (workflow) {
        loadVariables(workflow.variables);
      }
    } else if (workflows.length > 0 && !selectedWorkflowId) {
      // Auto-select first workflow
      setSelectedWorkflowId(workflows[0].id);
      loadVariables(workflows[0].variables);
    }
  }, [searchParams, workflows]);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      const response = await workflowsApi.list({ limit: 200 });
      // Handle different response structures (new paginated shape: { items, totalCount })
      const respData = response.data;
      const workflowsData = Array.isArray(respData?.items)
        ? respData.items
        : (Array.isArray(respData) ? respData : respData?.workflows || []);
      setWorkflows(workflowsData);
    } catch (error) {
      console.error('Failed to load workflows:', error);
      toast.error('Failed to load workflows');
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadVariables = (variables: Record<string, any>) => {
    const varsArray: Variable[] = Object.entries(variables || {}).map(([key, value]) => ({
      key,
      value,
      type: inferType(value),
    }));
    setLocalVariables(varsArray);
    setHasChanges(false);
  };

  const inferType = (value: any): Variable['type'] => {
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'object') return 'json';
    return 'string';
  };

  const handleWorkflowChange = (workflowId: string) => {
    if (hasChanges) {
      if (!confirm('You have unsaved changes. Do you want to discard them?')) {
        return;
      }
    }
    setSelectedWorkflowId(workflowId);
    setSearchParams({ workflow: workflowId });
    const workflow = workflows.find(w => w.id === workflowId);
    if (workflow) {
      loadVariables(workflow.variables);
    }
  };

  const handleAddVariable = () => {
    setLocalVariables([
      ...localVariables,
      { key: '', value: '', type: 'string' },
    ]);
    setHasChanges(true);
  };

  const handleRemoveVariable = (index: number) => {
    setLocalVariables(localVariables.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleUpdateVariable = (
    index: number,
    field: keyof Variable,
    value: any
  ) => {
    const updated = [...localVariables];
    updated[index] = { ...updated[index], [field]: value };

    // If type changes, convert the value
    if (field === 'type') {
      updated[index].value = convertValue(updated[index].value, value);
    }

    setLocalVariables(updated);
    setHasChanges(true);
  };

  const convertValue = (value: any, toType: Variable['type']): any => {
    try {
      switch (toType) {
        case 'string':
          return String(value);
        case 'number':
          return Number(value) || 0;
        case 'boolean':
          return Boolean(value);
        case 'json':
          return typeof value === 'string' ? JSON.parse(value) : value;
        default:
          return value;
      }
    } catch {
      return value;
    }
  };

  const handleSave = async () => {
    if (!selectedWorkflowId) return;

    // Validate variables
    const errors: string[] = [];
    const varsObject: Record<string, any> = {};

    localVariables.forEach((variable, index) => {
      if (!variable.key.trim()) {
        errors.push(`Variable ${index + 1}: Key is required`);
        return;
      }

      if (varsObject[variable.key]) {
        errors.push(`Variable "${variable.key}": Duplicate key`);
        return;
      }

      // Validate JSON
      if (variable.type === 'json') {
        try {
          const parsed =
            typeof variable.value === 'string'
              ? JSON.parse(variable.value)
              : variable.value;
          varsObject[variable.key] = parsed;
        } catch {
          errors.push(`Variable "${variable.key}": Invalid JSON`);
        }
      } else {
        varsObject[variable.key] = convertValue(variable.value, variable.type);
      }
    });

    if (errors.length > 0) {
      toast.error(
        <div>
          <div className="font-semibold">Validation errors:</div>
          <ul className="list-disc list-inside">
            {errors.map((error, i) => (
              <li key={i}>{error}</li>
            ))}
          </ul>
        </div>
      );
      return;
    }

    setSaving(true);
    try {
      const workflow = workflows.find(w => w.id === selectedWorkflowId);
      if (!workflow) return;

      await workflowsApi.update(selectedWorkflowId, {
        ...workflow,
        staticData: varsObject,
      });

      setHasChanges(false);
      toast.success('Variables saved successfully');
      
      // Reload workflows to get updated data
      await loadWorkflows();
    } catch (error) {
      toast.error('Failed to save variables');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const workflow = workflows.find(w => w.id === selectedWorkflowId);
    if (workflow) {
      loadVariables(workflow.variables);
    }
  };

  const handleExportJSON = () => {
    const varsObject: Record<string, any> = {};
    localVariables.forEach(v => {
      varsObject[v.key] = v.value;
    });
    
    const dataStr = JSON.stringify(varsObject, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `variables-${selectedWorkflowId}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Variables exported');
  };

  const handleExportCSV = () => {
    const csv = [
      ['Key', 'Value', 'Type'].join(','),
      ...localVariables.map(v => [
        v.key,
        typeof v.value === 'object' ? JSON.stringify(v.value) : v.value,
        v.type
      ].join(','))
    ].join('\n');
    
    const dataBlob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `variables-${selectedWorkflowId}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Variables exported');
  };

  const handleImportJSON = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target?.result as string);
        const varsArray: Variable[] = Object.entries(json).map(([key, value]) => ({
          key,
          value,
          type: inferType(value),
        }));
        setLocalVariables(varsArray);
        setHasChanges(true);
        toast.success('Variables imported successfully');
      } catch (error) {
        toast.error('Invalid JSON file');
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  const filteredVariables = localVariables.filter(v => {
    const matchesSearch = v.key.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || v.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const selectedWorkflow = workflows.find(w => w.id === selectedWorkflowId);

  if (loading) {
    return <VariablesPageSkeleton />;
  }

  if (workflows.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold mb-2">No Workflows Found</h2>
          <p className="text-muted-foreground mb-4">
            Create a workflow first to manage its variables.
          </p>
          <Button onClick={() => window.location.href = '/workflows'}>
            Go to Workflows
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold">Workflow Variables</h1>
              <p className="text-muted-foreground mt-1">
                Manage static variables that can be used across all nodes in your workflows
              </p>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Info className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>
                    Variables are available throughout the workflow. Access them using{' '}
                    <code className="bg-muted px-1 rounded">{'{{$vars.variableName}}'}</code>
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Workflow Selector */}
          <div className="flex items-center gap-4">
            <div className="flex-1 max-w-md">
              <Label htmlFor="workflow-select" className="text-sm mb-2 block">
                Select Workflow
              </Label>
              <Select value={selectedWorkflowId || ''} onValueChange={handleWorkflowChange}>
                <SelectTrigger id="workflow-select">
                  <SelectValue placeholder="Select a workflow" />
                </SelectTrigger>
                <SelectContent>
                  {workflows.map((workflow) => (
                    <SelectItem key={workflow.id} value={workflow.id}>
                      {workflow.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedWorkflow && (
              <div className="flex items-center gap-2 mt-6">
                <Badge variant="secondary">
                  {localVariables.length} {localVariables.length === 1 ? 'variable' : 'variables'}
                </Badge>
                {hasChanges && (
                  <Badge variant="outline" className="border-orange-500 text-orange-500">
                    Unsaved changes
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full flex">
          {/* Variables List */}
          <div className="flex-1 flex flex-col">
            {/* Toolbar */}
            <div className="border-b bg-card p-4">
              <div className="flex items-center gap-2">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search variables..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[140px]">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="string">String</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="boolean">Boolean</SelectItem>
                    <SelectItem value="json">JSON</SelectItem>
                  </SelectContent>
                </Select>

                <Separator orientation="vertical" className="h-8" />

                <Button variant="outline" size="sm" onClick={handleAddVariable}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Variable
                </Button>

                <div className="flex items-center gap-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="outline" size="sm" onClick={handleExportJSON}>
                          <Download className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Export as JSON</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="outline" size="sm" onClick={handleExportCSV}>
                          <Download className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Export as CSV</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="outline" size="sm" asChild>
                          <label htmlFor="import-json" className="cursor-pointer">
                            <Upload className="h-4 w-4" />
                            <input
                              id="import-json"
                              type="file"
                              accept=".json"
                              className="hidden"
                              onChange={handleImportJSON}
                            />
                          </label>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Import from JSON</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </div>

            {/* Variables Grid */}
            <ScrollArea className="flex-1">
              <div className="p-6">
                {filteredVariables.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-muted-foreground mb-4">
                      {searchQuery || typeFilter !== 'all' ? (
                        <>
                          <p className="text-lg font-medium">No variables match your filters</p>
                          <p className="text-sm mt-1">Try adjusting your search or filter</p>
                        </>
                      ) : (
                        <>
                          <p className="text-lg font-medium">No variables defined</p>
                          <p className="text-sm mt-1">Click "Add Variable" to create one</p>
                        </>
                      )}
                    </div>
                    {!searchQuery && typeFilter === 'all' && (
                      <Button onClick={handleAddVariable}>
                        <Plus className="mr-2 h-4 w-4" />
                        Add Your First Variable
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredVariables.map((variable, index) => {
                      const actualIndex = localVariables.indexOf(variable);
                      return (
                        <Card key={actualIndex} className="relative">
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <Label htmlFor={`var-key-${actualIndex}`} className="text-xs text-muted-foreground">
                                  Variable Name
                                </Label>
                                <Input
                                  id={`var-key-${actualIndex}`}
                                  value={variable.key}
                                  onChange={(e) =>
                                    handleUpdateVariable(actualIndex, 'key', e.target.value)
                                  }
                                  placeholder="variableName"
                                  className="font-mono text-sm mt-1"
                                />
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveVariable(actualIndex)}
                                className="text-destructive hover:text-destructive ml-2"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {/* Type */}
                            <div>
                              <Label htmlFor={`var-type-${actualIndex}`} className="text-xs text-muted-foreground">
                                Type
                              </Label>
                              <Select
                                value={variable.type}
                                onValueChange={(value) =>
                                  handleUpdateVariable(
                                    actualIndex,
                                    'type',
                                    value as Variable['type']
                                  )
                                }
                              >
                                <SelectTrigger id={`var-type-${actualIndex}`} className="mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="string">String</SelectItem>
                                  <SelectItem value="number">Number</SelectItem>
                                  <SelectItem value="boolean">Boolean</SelectItem>
                                  <SelectItem value="json">JSON</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Value */}
                            <div>
                              <Label htmlFor={`var-value-${actualIndex}`} className="text-xs text-muted-foreground">
                                Value
                              </Label>
                              {variable.type === 'boolean' ? (
                                <Select
                                  value={String(variable.value)}
                                  onValueChange={(value) =>
                                    handleUpdateVariable(
                                      actualIndex,
                                      'value',
                                      value === 'true'
                                    )
                                  }
                                >
                                  <SelectTrigger id={`var-value-${actualIndex}`} className="mt-1">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="true">true</SelectItem>
                                    <SelectItem value="false">false</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : variable.type === 'json' ? (
                                <textarea
                                  id={`var-value-${actualIndex}`}
                                  value={
                                    typeof variable.value === 'string'
                                      ? variable.value
                                      : JSON.stringify(variable.value, null, 2)
                                  }
                                  onChange={(e) =>
                                    handleUpdateVariable(actualIndex, 'value', e.target.value)
                                  }
                                  placeholder='{"key": "value"}'
                                  className="w-full min-h-[80px] px-3 py-2 text-sm border rounded-md font-mono resize-y mt-1"
                                />
                              ) : (
                                <Input
                                  id={`var-value-${actualIndex}`}
                                  type={variable.type === 'number' ? 'number' : 'text'}
                                  value={variable.value}
                                  onChange={(e) =>
                                    handleUpdateVariable(
                                      actualIndex,
                                      'value',
                                      variable.type === 'number'
                                        ? Number(e.target.value)
                                        : e.target.value
                                    )
                                  }
                                  placeholder={
                                    variable.type === 'number' ? '0' : 'Enter value'
                                  }
                                  className="mt-1"
                                />
                              )}
                            </div>

                            {/* Usage hint */}
                            {variable.key && (
                              <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                                <span className="font-semibold">Usage:</span>{' '}
                                <code className="bg-background px-1 rounded">
                                  {'{{$vars.' + variable.key + '}}'}
                                </code>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      {hasChanges && (
        <div className="border-t bg-card p-4">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <p className="text-sm text-muted-foreground">
              You have unsaved changes
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleReset}>
                <X className="mr-2 h-4 w-4" />
                Discard Changes
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
